const DESKTOP_BREAKPOINT = "(width >= 1024px)";
const SCROLL_START = 100;
const SCROLL_THRESHOLD = 5;
const SCROLL_THROTTLE_MS = 250;
const MENU_RESET_DELAY_MS = 500;

class LyleSiteHeader extends HTMLElement {
  #overlay = null;
  #menuShelf = null;
  #menuToggle = null;
  #resizeListener = null;
  #isOpen = false;
  #closeTimer = null;
  #resetTimer = null;
  #resizeObserver = null;
  #scroll = null;
  #scrollListener = null;
  #scrollTimer = null;
  #lastScrollAt = 0;

  connectedCallback() {
    this.#overlay = this.querySelector("[data-site-header-overlay]");
    this.#menuShelf = this.#overlay?.querySelector("lyle-menu-shelf");
    this.#menuToggle = this.querySelector("[data-site-header-menu-toggle]");

    this.#menuToggle?.addEventListener('click', this.#handleMenuToggle);
    this.addEventListener('mouseenter', this.#cancelClose);
    this.addEventListener('mouseleave', this.#handleMouseLeave);

    this.querySelectorAll("[data-menu-trigger]").forEach((link) => {
      link.addEventListener("mouseenter", this.#handleNavHover);
      link.addEventListener("focus", this.#handleNavHover);
    });

    document.addEventListener("keydown", this.#handleKeydown);
    this.addEventListener("header-search:open", this.#handleSearchOpen);
    this.addEventListener("header-search:close", this.#handleSearchClose);
    this.#scrollListener = () => this.#handleScrollThrottled();
    this.#resizeListener = () => {
      this.#setScrollbarWidth();
      this.#setHeights();
      if (this.#isOpen) {
        this.#close();
      }
    };
    window.addEventListener("resize", this.#resizeListener, { passive: true });
    window.addEventListener("scroll", this.#scrollListener, { passive: true });

    this.#setScrollbarWidth();
    this.#handleScroll(true);
    this.#setHeights();
    requestAnimationFrame(() => this.#setHeights());
    window.addEventListener("load", this.#setHeights, { once: true });

    if (typeof ResizeObserver !== "undefined") {
      this.#resizeObserver = new ResizeObserver(() => this.#setHeights());
      this.#resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#menuToggle?.removeEventListener('click', this.#handleMenuToggle);
    this.removeEventListener('mouseenter', this.#cancelClose);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    document.removeEventListener('keydown', this.#handleKeydown);
    window.removeEventListener('scroll', this.#scrollListener);
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('load', this.#setHeights);
    this.#resizeObserver?.disconnect();
    if (this.#scrollTimer) {
      clearTimeout(this.#scrollTimer);
    }
    document.body.classList.remove("site-header-menu-open");
  }

  #setHeights = () => {
    const root = this.closest(".site-header-container") || this;
    const masthead = this.querySelector(".site-header__masthead");
    const mobileSearch = this.querySelector(".header-search-form--mobile");
    const height = root.getBoundingClientRect().height;
    const chromeHeight =
      (masthead?.getBoundingClientRect().height || 0) +
      (mobileSearch?.getBoundingClientRect().height || 0);
    const visibleHeight = chromeHeight || height;

    document.documentElement.style.setProperty(
      "--header-height",
      `${height}px`,
    );
    document.documentElement.style.setProperty(
      "--header-visible-height",
      `${visibleHeight}px`,
    );
  };

  #setScrollbarWidth() {
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.remove();
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${scrollbarWidth}px`,
    );
  }

  #isDesktop() {
    return window.matchMedia(DESKTOP_BREAKPOINT).matches;
  }

  #setHeaderState(headerState) {
    this.classList.remove("is-open", "is-minimised", "is-collapsed");
    this.classList.add(`is-${headerState}`);
    this.#setHeights();
  }

  #handleScroll = (start = false) => {
    if (this.#isOpen) return;

    if (start) {
      this.#scroll = {
        direction: "none",
        header: "open",
        top: window.scrollY <= SCROLL_START,
        x: window.scrollX,
        y: window.scrollY,
      };
      this.#setHeaderState("open");
      return;
    }

    if (!this.#scroll) {
      this.#handleScroll(true);
      return;
    }

    const difference = window.scrollY - this.#scroll.y;

    if (Math.abs(difference) < SCROLL_THRESHOLD) return;

    const direction = difference > 0 ? "down" : "up";
    const top = window.scrollY <= SCROLL_START;
    let header = "open";

    if (direction === "up" && !top) {
      header = "minimised";
    } else if (window.scrollY > 0) {
      header = "collapsed";
    }

    this.#scroll = {
      direction,
      header,
      top,
      x: window.scrollX,
      y: window.scrollY,
    };

    this.#setHeaderState(header);
  };

  #handleScrollThrottled() {
    const now = Date.now();
    const remaining = SCROLL_THROTTLE_MS - (now - this.#lastScrollAt);

    if (remaining <= 0) {
      if (this.#scrollTimer) {
        clearTimeout(this.#scrollTimer);
        this.#scrollTimer = null;
      }

      this.#lastScrollAt = now;
      this.#handleScroll();
      return;
    }

    if (this.#scrollTimer) return;

    this.#scrollTimer = setTimeout(() => {
      this.#lastScrollAt = Date.now();
      this.#scrollTimer = null;
      this.#handleScroll();
    }, remaining);
  }

  #cancelClose = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  #cancelReset = () => {
    if (this.#resetTimer) {
      clearTimeout(this.#resetTimer);
      this.#resetTimer = null;
    }
  };

  #handleMenuToggle = () => {
    if (this.#isOpen) {
      this.#close();
    } else {
      this.#open({ mode: "mobile" });
    }
  };

  #handleSearchOpen = () => {
    if (this.#isOpen) {
      this.#close();
    }
  };

  #handleSearchClose = (event) => {
    if (event.detail?.restoreMenu && !this.#isDesktop() && !this.#isOpen) {
      this.#open({ mode: "mobile" });
    }
  };

  #handleNavHover = (event) => {
    if (!this.#isDesktop()) return;

    const link = event.currentTarget;
    const menuKey = link.dataset.menuTrigger;
    const hasChildren = link.dataset.menuHasChildren === "true";

    this.#cancelClose();

    if (!hasChildren || !menuKey) {
      this.#scheduleClose();
      return;
    }

    this.#open({ mode: "desktop", menuKey });
  };

  #handleMouseLeave = () => {
    if (!this.#isDesktop()) return;
    this.#scheduleClose();
  };

  #scheduleClose() {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => this.#close(), 120);
  }

  #handleKeydown = (event) => {
    if (event.key === "Escape" && this.#isOpen) {
      this.#close();
    }
  };

  #open({ mode, menuKey }) {
    if (!this.#overlay) return;

    this.#cancelReset();
    this.#isOpen = true;
    this.#overlay.classList.remove("is-hidden");
    this.#overlay.hidden = false;
    this.#overlay.setAttribute("aria-hidden", "false");
    this.classList.add("site-header--menu-open");
    this.#setHeaderState("open");
    document.body.classList.add("site-header-menu-open");
    this.#menuToggle?.setAttribute("aria-expanded", "true");

    if (mode === "desktop" && menuKey) {
      this.#menuShelf?.activateDesktopPanel(menuKey);
    } else {
      this.#menuShelf?.reset();
      this.#menuShelf?.classList.add("is-active");
    }

    this.dispatchEvent(
      new CustomEvent("site-header:menu-open", {
        bubbles: false,
      }),
    );
    this.#setHeights();
  }

  #close = () => {
    if (!this.#overlay || !this.#isOpen) return;

    this.#isOpen = false;
    this.#overlay.classList.add("is-hidden");
    this.#overlay.setAttribute("aria-hidden", "true");
    this.classList.remove("site-header--menu-open");
    document.body.classList.remove("site-header-menu-open");
    this.#menuToggle?.setAttribute("aria-expanded", "false");
    this.#resetTimer = setTimeout(() => {
      this.#menuShelf?.reset();
      this.#resetTimer = null;
    }, MENU_RESET_DELAY_MS);
    this.#cancelClose();
    this.#handleScroll();
  };
}

if (!customElements.get("lyle-site-header")) {
  customElements.define("lyle-site-header", LyleSiteHeader);
}
